import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';

// Version tracking
const APP_VERSION = '2.1.0';
const BUILD_DATE = '2025-12-14T03:30:00Z';
const FEATURES = ['Collapsible Sidebar', 'Security Center', 'Enhanced Toggle Button'];

// Import components
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DashboardLayout from './layouts/DashboardLayout';
import AdminDashboard from './pages/AdminDashboard';
import CustomerDashboard from './pages/CustomerDashboard';
import ProfilePage from './pages/ProfilePage';
import ProductsPage from './pages/ProductsPage';
import MyLicenseRequests from './pages/MyLicenseRequests';
import AdminLicenseRequests from './pages/AdminLicenseRequests';
import UsersPage from './pages/UsersPage';
import ActivationKeysPage from './pages/ActivationKeysPage';
import AdminActivationKeys from './pages/AdminActivationKeys';
import ClusterLicensesPage from './pages/ClusterLicensesPage';
import AdminClusterLicenses from './pages/AdminClusterLicenses';
import AdminXNASLicenses from './pages/AdminXNASLicenses';
import AdminNxtvaultLicenses from './pages/AdminNxtvaultLicenses';
import MyRequestsPage from './pages/MyRequestsPage';
import MyLicensesPage from './pages/MyLicensesPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import CustomerOverview from './pages/CustomerOverview';
import AdminSystemSettings from './pages/AdminSystemSettings';
import TwoFactorSettings from './pages/TwoFactorSettings';
import SecurityDashboard from './pages/SecurityDashboard';

// Import styles
import './styles/globals.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, customerOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Note: Don't check emailVerified here for authenticated users
  // Backend already blocks login for unverified users
  // This check would prevent already-logged-in users from accessing verify-email page

  if (adminOnly && user.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  if (customerOnly && user.role !== 'CUSTOMER') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={<VerifyEmail />}
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Nested Dashboard Routes */}
              <Route index element={<DashboardRedirect />} />
              <Route path="overview" element={
                <ProtectedRoute customerOnly>
                  <CustomerOverview />
                </ProtectedRoute>
              } />
              <Route path="admin" element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="customer" element={<CustomerDashboard />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="activation-keys" element={
                <ProtectedRoute customerOnly>
                  <ActivationKeysPage />
                </ProtectedRoute>
              } />
              <Route path="cluster-licenses" element={
                <ProtectedRoute customerOnly>
                  <ClusterLicensesPage />
                </ProtectedRoute>
              } />
              <Route path="my-requests" element={
                <ProtectedRoute customerOnly>
                  <MyRequestsPage />
                </ProtectedRoute>
              } />
              <Route path="my-licenses" element={
                <ProtectedRoute customerOnly>
                  <MyLicensesPage />
                </ProtectedRoute>
              } />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="account-settings" element={<AccountSettingsPage />} />
              <Route path="two-factor" element={<TwoFactorSettings />} />
              <Route path="security" element={<SecurityDashboard />} />
              <Route path="requests" element={<MyLicenseRequests />} />
              <Route path="users" element={
                <ProtectedRoute adminOnly>
                  <UsersPage />
                </ProtectedRoute>
              } />
              <Route path="admin/requests" element={
                <ProtectedRoute adminOnly>
                  <AdminLicenseRequests />
                </ProtectedRoute>
              } />
              <Route path="admin/activation-keys" element={
                <ProtectedRoute adminOnly>
                  <AdminActivationKeys />
                </ProtectedRoute>
              } />
              <Route path="admin/cluster-licenses" element={
                <ProtectedRoute adminOnly>
                  <AdminClusterLicenses />
                </ProtectedRoute>
              } />
              <Route path="admin/xnas-licenses" element={
                <ProtectedRoute adminOnly>
                  <AdminXNASLicenses />
                </ProtectedRoute>
              } />
              <Route path="admin/nxtvault-licenses" element={
                <ProtectedRoute adminOnly>
                  <AdminNxtvaultLicenses />
                </ProtectedRoute>
              } />
              <Route path="admin/system-settings" element={
                <ProtectedRoute adminOnly>
                  <AdminSystemSettings />
                </ProtectedRoute>
              } />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>

          {/* Global Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#059669',
                },
              },
              error: {
                style: {
                  background: '#DC2626',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

// Component to redirect based on user role
const DashboardRedirect = () => {
  const { user } = useAuth();
  
  if (user?.role === 'ADMIN') {
    return <Navigate to="/dashboard/admin" replace />;
  } else {
    return <Navigate to="/dashboard/overview" replace />;
  }
};

export default App;